import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, UserCog } from "lucide-react";
import { useState } from "react";
import { ApiError } from "@hostdock/sdk";
import type { AdminRecord, CreatedWithPassword } from "@hostdock/types";
import {
  ActionMenu, Badge, Button, ConfirmDialog, DataTable, Dialog, EmptyState, Field, FormError, formatRelative, Input, OneTimeSecret,
  PageHeader, Pagination, Panel, Select, StatusBadge, toast,
} from "@hostdock/ui";
import { api } from "../api";
import { RequirePermission } from "../lib/guard";
import { useListParams } from "../lib/list-params";
import { useSession } from "../session";

const PAGE_SIZE = 25;
type Action = "role" | "disable" | "enable" | "reset-password" | "reset-mfa" | "revoke";

export function AdminsPage() {
  return (
    <RequirePermission anyOf={["SECURITY_MANAGE"]}>
      <Admins />
    </RequirePermission>
  );
}

function Admins() {
  const { session } = useSession();
  const qc = useQueryClient();
  const { values, page, set } = useListParams(["search", "status"] as const);
  const [search, setSearch] = useState(values.search);
  const [creating, setCreating] = useState(false);
  const [target, setTarget] = useState<{ admin: AdminRecord; action: Action } | null>(null);
  const [secret, setSecret] = useState<CreatedWithPassword<AdminRecord> | null>(null);
  const q = useQuery({ queryKey: ["admins", values, page], queryFn: () => api.admins.list({ ...values, page, pageSize: PAGE_SIZE }), placeholderData: keepPreviousData });

  const refresh = () => void qc.invalidateQueries({ queryKey: ["admins"] });
  const finish = (msg: string) => {
    toast.success(msg);
    setTarget(null);
    refresh();
  };
  const id = target?.admin.id ?? "";
  const disable = useMutation({ meta: { inlineError: true }, mutationFn: (reason: string) => api.admins.disable(id, reason), onSuccess: () => finish("Admin disabled and signed out") });
  const enable = useMutation({ meta: { inlineError: true }, mutationFn: () => api.admins.enable(id), onSuccess: () => finish("Admin enabled") });
  const resetPw = useMutation({ meta: { inlineError: true }, mutationFn: () => api.admins.resetPassword(id), onSuccess: (r) => { setTarget(null); setSecret(r); refresh(); } });
  const resetMfa = useMutation({ meta: { inlineError: true }, mutationFn: (reason: string) => api.admins.resetMfa(id, reason), onSuccess: () => finish("Two-factor authentication reset") });
  const revoke = useMutation({ meta: { inlineError: true }, mutationFn: () => api.admins.revokeSessions(id), onSuccess: (r) => finish(`Signed out ${r.revoked} session${r.revoked === 1 ? "" : "s"}`) });
  const open = (a: Action) => target?.action === a;
  const onClose = (o: boolean) => {
    if (!o) {
      setTarget(null);
      [disable, enable, resetPw, resetMfa, revoke].forEach((m) => m.reset());
    }
  };

  return (
    <>
      <PageHeader title="Admins" description="People who can access HostDock Admin." actions={<Button variant="primary" icon={<Plus size={15} />} onClick={() => setCreating(true)}>Add admin</Button>} />
      <Panel flush>
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
          <form className="relative min-w-52 flex-1" onSubmit={(e) => { e.preventDefault(); set({ search: search.trim() }); }}>
            <Search size={14} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-subtle" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email" className="pl-8" aria-label="Search admins" />
          </form>
          <Select value={values.status} onChange={(e) => set({ status: e.target.value })} className="w-36" aria-label="Filter by status">
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="DISABLED">Disabled</option>
          </Select>
        </div>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(a) => a.id}
          empty={<EmptyState icon={UserCog} title="No admins match" />}
          columns={[
            {
              key: "name",
              header: "Admin",
              cell: (a) => (
                <div className="min-w-0">
                  <p className="truncate font-medium">
                    {a.name} {a.userId === session.user.id && <Badge tone="accent">You</Badge>}
                  </p>
                  <p className="truncate text-[12px] text-muted">{a.email}</p>
                </div>
              ),
            },
            { key: "role", header: "Role", cell: (a) => a.role.name },
            { key: "status", header: "Status", cell: (a) => <StatusBadge status={a.status} /> },
            { key: "mfa", header: "2FA", cell: (a) => <Badge tone={a.mfaEnabled ? "success" : "warning"}>{a.mfaEnabled ? "On" : "Off"}</Badge>, hideOnMobile: true },
            { key: "login", header: "Last sign-in", cell: (a) => <span className="text-muted">{a.lastLoginAt ? formatRelative(a.lastLoginAt) : "Never"}</span>, hideOnMobile: true },
            {
              key: "actions",
              header: "",
              className: "w-10 text-right",
              cell: (a) =>
                a.userId === session.user.id ? null : (
                  <ActionMenu
                    actions={[
                      { label: "Change role", onSelect: () => setTarget({ admin: a, action: "role" }) },
                      { label: "Reset password", onSelect: () => setTarget({ admin: a, action: "reset-password" }) },
                      { label: "Reset two-factor", onSelect: () => setTarget({ admin: a, action: "reset-mfa" }), hidden: !a.mfaEnabled },
                      { label: "Sign out all sessions", onSelect: () => setTarget({ admin: a, action: "revoke" }) },
                      { label: "Enable", onSelect: () => setTarget({ admin: a, action: "enable" }), hidden: a.status === "ACTIVE" },
                      { label: "Disable", onSelect: () => setTarget({ admin: a, action: "disable" }), hidden: a.status !== "ACTIVE", danger: true },
                    ]}
                  />
                ),
            },
          ]}
        />
        {q.data && <Pagination page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={(p) => set({ page: String(p) })} />}
      </Panel>

      <CreateAdminDialog open={creating} onOpenChange={setCreating} onCreated={(r) => { setSecret(r); refresh(); }} />
      {target?.action === "role" && <ChangeRoleDialog admin={target.admin} onClose={() => setTarget(null)} onSaved={() => finish("Role updated")} />}
      <ConfirmDialog open={open("disable")} onOpenChange={onClose} title={`Disable ${target?.admin.name}?`} description="They are signed out immediately and cannot sign in until re-enabled." confirmLabel="Disable admin" confirmText={target?.admin.email} requireReason loading={disable.isPending} error={disable.error} onConfirm={(r) => disable.mutate(r)} />
      <ConfirmDialog open={open("enable")} onOpenChange={onClose} tone="primary" title={`Enable ${target?.admin.name}?`} confirmLabel="Enable" loading={enable.isPending} error={enable.error} onConfirm={() => enable.mutate()} />
      <ConfirmDialog open={open("reset-password")} onOpenChange={onClose} title={`Reset password for ${target?.admin.name}?`} description="A temporary password is generated and all their sessions are signed out." confirmLabel="Reset password" loading={resetPw.isPending} error={resetPw.error} onConfirm={() => resetPw.mutate()} />
      <ConfirmDialog open={open("reset-mfa")} onOpenChange={onClose} title={`Reset two-factor for ${target?.admin.name}?`} description="Verify their identity out-of-band first." confirmLabel="Reset two-factor" requireReason loading={resetMfa.isPending} error={resetMfa.error} onConfirm={(r) => resetMfa.mutate(r)} />
      <ConfirmDialog open={open("revoke")} onOpenChange={onClose} title={`Sign out ${target?.admin.name} everywhere?`} confirmLabel="Sign out" loading={revoke.isPending} error={revoke.error} onConfirm={() => revoke.mutate()} />
      <Dialog open={secret !== null} onOpenChange={(o) => !o && setSecret(null)} title="Temporary password" footer={<Button variant="primary" onClick={() => setSecret(null)}>Done</Button>}>
        {secret && <OneTimeSecret label={`Temporary password for ${secret.record.email}`} value={secret.temporaryPassword} note="They must change it and set up two-factor authentication (if required) at first sign-in." />}
      </Dialog>
    </>
  );
}

function useRoles() {
  return useQuery({ queryKey: ["roles"], queryFn: api.roles.list });
}

function CreateAdminDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange(o: boolean): void; onCreated(r: CreatedWithPassword<AdminRecord>): void }) {
  const roles = useRoles();
  const [form, setForm] = useState({ name: "", email: "", roleId: "" });
  const create = useMutation({
    mutationFn: () => api.admins.create(form),
    onSuccess: (r) => {
      close(false);
      onCreated(r);
    },
  });
  const close = (o: boolean) => {
    if (!o) {
      setForm({ name: "", email: "", roleId: "" });
      create.reset();
    }
    onOpenChange(o);
  };
  const fe = create.error instanceof ApiError ? create.error.fieldErrors : {};
  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title="Add admin"
      description="They receive a temporary password and must change it at first sign-in."
      footer={
        <>
          <Button variant="ghost" onClick={() => close(false)}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} disabled={!form.name || !form.email || !form.roleId} onClick={() => create.mutate()}>Add admin</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Full name" error={fe.name}>{(p) => <Input {...p} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />}</Field>
        <Field label="Email" error={fe.email}>{(p) => <Input {...p} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="off" />}</Field>
        <Field label="Role" error={fe.roleId} hint="You can only assign roles whose permissions you hold.">
          {(p) => (
            <Select {...p} value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value })} disabled={roles.isPending}>
              <option value="">Select a role…</option>
              {roles.data?.items.map((r) => (
                <option key={r.id} value={r.id}>{r.name} — {r.permissions.length} permission{r.permissions.length === 1 ? "" : "s"}</option>
              ))}
            </Select>
          )}
        </Field>
        <FormError error={roles.error ?? (Object.keys(fe).length ? null : create.error)} />
      </div>
    </Dialog>
  );
}

function ChangeRoleDialog({ admin, onClose, onSaved }: { admin: AdminRecord; onClose(): void; onSaved(): void }) {
  const roles = useRoles();
  const [roleId, setRoleId] = useState(admin.role.id);
  const save = useMutation({ mutationFn: () => api.admins.update(admin.id, { roleId }), onSuccess: onSaved });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Change role for ${admin.name}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={roleId === admin.role.id} onClick={() => save.mutate()}>Save</Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Role">
          {(p) => (
            <Select {...p} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
              {roles.data?.items.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </Select>
          )}
        </Field>
        <FormError error={roles.error ?? save.error} />
      </div>
    </Dialog>
  );
}
